import React, { useState, useEffect } from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    BarChart, Bar, PieChart, Pie, Cell, RadarChart, PolarGrid, PolarAngleAxis,
    PolarRadiusAxis, Radar
} from 'recharts';

const DigitalTransformationDashboard = ({ onAssessmentSelect, selectedAssessmentId, onCreateNewAssessment, onEditAssessment }) => {
    const [activeTab, setActiveTab] = useState('overview');
    const [assessments, setAssessments] = useState([]);
    const [selectedAssessment, setSelectedAssessment] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [isComparisonMode, setIsComparisonMode] = useState(false);
    const [comparisonIds, setComparisonIds] = useState([]);
    const [comparisonData, setComparisonData] = useState([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [assessmentToDelete, setAssessmentToDelete] = useState(null);
    // New state to control view mode
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'detail'

    const apiUrl = 'https://internalassessmentsyngroups.com/digital-transformation/api'; // Update with your API URL

    // Fetch list of assessments
    useEffect(() => {
        fetchAssessments();
    }, []);

    // Fetch selected assessment data when ID changes or view mode changes
    useEffect(() => {
        if (selectedAssessmentId && viewMode === 'detail' && !isComparisonMode) {
            fetchAssessmentData(selectedAssessmentId);
        } else if (!selectedAssessmentId || viewMode === 'list') {
            setSelectedAssessment(null);
        }
    }, [selectedAssessmentId, viewMode, isComparisonMode]);

    // Fetch comparison data when comparison IDs change AND comparison mode is active
    // This useEffect will NOT automatically fetch data when IDs change
    useEffect(() => {
        if (isComparisonMode && comparisonIds.length > 0 && viewMode === 'detail') {
            fetchComparisonData();
        }
    }, [isComparisonMode, comparisonIds, viewMode]);

    const fetchAssessments = async () => {
        try {
            setLoading(true);
            const response = await fetch(`https://internalassessmentsyngroups.com/digital-transformation/api/get_assessment_data.php`);
            const data = await response.json();

            if (data.success) {
                setAssessments(data.assessments);

                // If selectedAssessmentId exists and viewMode is 'detail', 
                // we'll load that assessment, otherwise we stay in list view
                if (selectedAssessmentId && viewMode === 'detail') {
                    fetchAssessmentData(selectedAssessmentId);
                }
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to fetch assessments. Please check your connection.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchAssessmentData = async (id) => {
        try {
            setLoading(true);
            const response = await fetch(`https://internalassessmentsyngroups.com/digital-transformation/api/get_assessment_data.php?id=${id}`);
            const data = await response.json();

            if (data.success) {
                setSelectedAssessment(data);
                setViewMode('detail');
            } else {
                setError(data.message);
                setViewMode('list');
            }
        } catch (err) {
            setError('Failed to fetch assessment data. Please check your connection.');
            console.error(err);
            setViewMode('list');
        } finally {
            setLoading(false);
        }
    };

    const fetchComparisonData = async () => {
        try {
            setLoading(true);
            const comparisonResults = [];

            // Fetch data for each selected assessment
            for (const id of comparisonIds) {
                const response = await fetch(`https://internalassessmentsyngroups.com/digital-transformation/api/get_assessment_data.php?id=${id}`);
                const data = await response.json();

                if (data.success) {
                    comparisonResults.push(data);
                }
            }

            setComparisonData(comparisonResults);
            setViewMode('detail'); // Switch to detail view when comparison data is loaded
        } catch (err) {
            setError('Failed to fetch comparison data. Please check your connection.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAssessment = async () => {
        if (!assessmentToDelete) return;

        try {
            setLoading(true);
            setError(null);

            const response = await fetch(`https://internalassessmentsyngroups.com/digital-transformation/api/delete_assessment.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ id: assessmentToDelete })
            });

            const result = await response.json();

            if (result.success) {
                setSuccessMessage(`Assessment deleted successfully.`);

                // ลบข้อมูลแบบประเมินออกจาก state
                setAssessments(prevAssessments =>
                    prevAssessments.filter(assessment => assessment.id !== assessmentToDelete)
                );

                // ถ้าแบบประเมินที่ลบคือแบบประเมินที่กำลังดูอยู่
                if (selectedAssessmentId === assessmentToDelete) {
                    setSelectedAssessment(null);
                    onAssessmentSelect(null);
                    setViewMode('list'); // กลับไปที่หน้ารายการแบบประเมิน
                }

                // ลบออกจากรายการเปรียบเทียบ (ถ้ามี)
                if (comparisonIds.includes(assessmentToDelete)) {
                    setComparisonIds(prevIds => prevIds.filter(id => id !== assessmentToDelete));

                    // ถ้ากำลังอยู่ในโหมดเปรียบเทียบ ให้อัปเดตข้อมูลเปรียบเทียบด้วย
                    if (isComparisonMode && comparisonData.length > 0) {
                        setComparisonData(prevData =>
                            prevData.filter(data => data.assessment.id !== assessmentToDelete)
                        );
                    }
                }

                // ปิดหน้าต่างยืนยันการลบ
                setShowDeleteConfirm(false);
                setAssessmentToDelete(null);

                // แสดงข้อความสำเร็จเป็นเวลา 3 วินาที
                setTimeout(() => {
                    setSuccessMessage('');
                }, 3000);

                // กลับไปที่หน้ารายการแบบประเมิน
                setViewMode('list');
            } else {
                setError(result.message || 'Failed to delete assessment');
            }
        } catch (err) {
            setError('Error deleting assessment. Please check your connection.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    const toggleComparisonMode = () => {
        if (!isComparisonMode) {
            // Entering comparison mode - clear previous selections
            setIsComparisonMode(true);
            setComparisonIds([]); // Clear previous selections
            setComparisonData([]); // Clear previous comparison data
            setViewMode('list'); // Start in list view when entering comparison mode
        } else {
            // Exiting comparison mode
            setIsComparisonMode(false);
            setComparisonData([]);
            setComparisonIds([]); // Clear selected comparison IDs
            setViewMode('list'); // Go back to list view when exiting comparison mode
            // If we have a previously selected assessment, don't automatically load it
            if (selectedAssessmentId) {
                onAssessmentSelect(null);
            }
        }
    };

    const handleComparisonChange = (e) => {
        const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
        setComparisonIds(selectedOptions);
    };

    // New function to initiate comparison after selecting assessments
    const handleCompareSelectedAssessments = () => {
        if (comparisonIds.length > 0) {
            fetchComparisonData();
        } else {
            setError('Please select at least one assessment to compare.');
            setTimeout(() => {
                setError(null);
            }, 3000);
        }
    };

    const handleViewSingleAssessment = () => {
        if (selectedAssessmentId) {
            fetchAssessmentData(selectedAssessmentId);
            setViewMode('detail');
        }
    };

    // Handler for selecting an assessment from the list
    const handleSelectAssessment = (id) => {
        onAssessmentSelect(id);
        // Don't automatically fetch the assessment data, wait for user to click button
    };

    // COLORS for charts
    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];
    const PRIORITY_COLORS = {
        'H': '#FF5252',
        'M': '#FFB300',
        'L': '#66BB6A'
    };
    const SECTION_COLORS = {
        'hardware': '#0088FE',
        'network': '#00C49F',
        'software': '#FFBB28',
        'dataInfrastructure': '#FF8042'
    };

    // Render loading or error states
    if (loading && !selectedAssessment && comparisonData.length === 0) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                    <p className="mt-2">Loading dashboard data...</p>
                </div>
            </div>
        );
    }

    if (error && !selectedAssessment && comparisonData.length === 0) {
        return (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
                <strong className="font-bold">Error!</strong>
                <span className="block sm:inline"> {error}</span>
            </div>
        );
    }

    // Render success message if present
    const renderSuccessMessage = () => {
        if (successMessage) {
            return (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4" role="alert">
                    <strong className="font-bold">Success!</strong>
                    <span className="block sm:inline"> {successMessage}</span>
                </div>
            );
        }
        return null;
    };

    // Render error message if present
    const renderErrorMessage = () => {
        if (error) {
            return (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
                    <strong className="font-bold">Error!</strong>
                    <span className="block sm:inline"> {error}</span>
                </div>
            );
        }
        return null;
    };

    // Render delete confirmation modal
    const renderDeleteConfirmModal = () => {
        if (!showDeleteConfirm) return null;

        const assessmentName = assessments.find(a => a.id === assessmentToDelete)?.assessment_name || "this assessment";

        return (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
                    <h3 className="text-xl font-bold mb-4">Confirm Deletion</h3>
                    <p className="mb-6">Are you sure you want to delete "{assessmentName}"? This action cannot be undone.</p>

                    <div className="flex justify-end space-x-3">
                        <button
                            className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
                            onClick={() => {
                                setShowDeleteConfirm(false);
                                setAssessmentToDelete(null);
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                            onClick={handleDeleteAssessment}
                        >
                            Delete
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Render assessment selection (list view)
    const renderAssessmentList = () => {
        return (
            <div className="p-6">
                {renderSuccessMessage()}
                {renderErrorMessage()}
                {renderDeleteConfirmModal()}

                {assessments.length === 0 ? (
                    <div className="text-center p-8">
                        <p className="mb-4">No assessments found. Create your first assessment to get started.</p>
                        <button
                            className="px-6 py-2 bg-blue-600 text-white font-semibold rounded hover:bg-blue-700"
                            onClick={() => window.location.href = "https://internalassessmentsyngroups.com"}
                        >
                            Create New Assessment
                        </button>

                    </div>
                ) : (
                    <div className="text-center p-4">
                        <p className="mb-4">Select an assessment to view its dashboard or compare multiple assessments:</p>

                        <div className="flex justify-center mb-4">
                            <button
                                className={`px-4 py-2 mr-2 ${!isComparisonMode ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'} rounded`}
                                onClick={() => setIsComparisonMode(false)}
                            >
                                View Single Assessment
                            </button>
                            <button
                                className={`px-4 py-2 ${isComparisonMode ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'} rounded`}
                                onClick={() => setIsComparisonMode(true)}
                            >
                                Compare Assessments
                            </button>
                        </div>

                        {isComparisonMode ? (
                            <div className="mb-4">
                                <select
                                    multiple
                                    className="w-full max-w-md p-2 border rounded"
                                    size={Math.min(6, assessments.length)}
                                    onChange={handleComparisonChange}
                                    value={comparisonIds}
                                >
                                    {assessments.map(assessment => (
                                        <option key={assessment.id} value={assessment.id}>
                                            {assessment.assessment_name} - {assessment.company_name} ({new Date(assessment.assessment_date).toLocaleDateString()})
                                        </option>
                                    ))}
                                </select>
                                <p className="text-sm text-gray-600 mt-1">Hold Ctrl/Cmd to select multiple assessments</p>

                                {/* Add button to trigger comparison after selection */}
                                <button
                                    className="mt-4 px-6 py-2 bg-blue-600 text-white font-semibold rounded hover:bg-blue-700"
                                    onClick={handleCompareSelectedAssessments}
                                    disabled={comparisonIds.length === 0}
                                >
                                    Compare Selected Assessments
                                </button>
                            </div>
                        ) : (
                            <div>
                                <div className="flex flex-col space-y-2 max-w-md mx-auto">
                                    {assessments.map(assessment => (
                                        <div key={assessment.id} className="flex items-center">
                                            <button
                                                className={`flex-grow px-4 py-2 ${selectedAssessmentId === assessment.id ? 'bg-blue-100 border-blue-400 border-2' : 'bg-gray-200 hover:bg-gray-300'} rounded text-left`}
                                                onClick={() => handleSelectAssessment(assessment.id)}
                                            >
                                                <span className="font-semibold">{assessment.assessment_name}</span>
                                                <span className="block text-sm text-gray-600">
                                                    {assessment.company_name} - {new Date(assessment.assessment_date).toLocaleDateString()}
                                                </span>
                                            </button>
                                            <button
                                                className="ml-2 p-2 bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
                                                onClick={() => {
                                                    onEditAssessment(assessment.id);
                                                }}
                                                title="Edit assessment"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                                </svg>
                                            </button>
                                            <button
                                                className="ml-1 p-2 bg-red-100 text-red-800 rounded hover:bg-red-200"
                                                onClick={() => {
                                                    setAssessmentToDelete(assessment.id);
                                                    setShowDeleteConfirm(true);
                                                }}
                                                title="Delete assessment"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                {/* Add button to view the selected assessment */}
                                {selectedAssessmentId && (
                                    <div className="mt-4">
                                        <button
                                            className="px-6 py-2 bg-blue-600 text-white font-semibold rounded hover:bg-blue-700"
                                            onClick={handleViewSingleAssessment}
                                        >
                                            View Selected Assessment
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Prepare data for charts
    const prepareRadarData = () => {
        if (isComparisonMode) {
            // สร้าง unique keys สำหรับแต่ละ assessment และแยกระหว่าง current กับ target
            const assessmentData = comparisonData.map((data, index) => ({
                name: data.assessment.assessment_name,
                currentKey: `${data.assessment.assessment_name}_current_${index}`,
                targetKey: `${data.assessment.assessment_name}_target_${index}`,
                current: {
                    hardware: data.statistics.overallScores.hardware.current,
                    network: data.statistics.overallScores.network.current,
                    software: data.statistics.overallScores.software.current,
                    dataInfra: data.statistics.overallScores.dataInfrastructure.current
                },
                target: {
                    hardware: data.statistics.overallScores.hardware.target,
                    network: data.statistics.overallScores.network.target,
                    software: data.statistics.overallScores.software.target,
                    dataInfra: data.statistics.overallScores.dataInfrastructure.target
                }
            }));

            return [
                {
                    subject: 'Hardware',
                    ...assessmentData.reduce((acc, data) => {
                        // เพิ่มทั้ง current และ target ด้วย keys ที่ไม่ซ้ำกัน
                        acc[data.currentKey] = data.current.hardware;
                        acc[data.targetKey] = data.target.hardware;
                        return acc;
                    }, {})
                },
                {
                    subject: 'Network',
                    ...assessmentData.reduce((acc, data) => {
                        acc[data.currentKey] = data.current.network;
                        acc[data.targetKey] = data.target.network;
                        return acc;
                    }, {})
                },
                {
                    subject: 'Software',
                    ...assessmentData.reduce((acc, data) => {
                        acc[data.currentKey] = data.current.software;
                        acc[data.targetKey] = data.target.software;
                        return acc;
                    }, {})
                },
                {
                    subject: 'Data',
                    ...assessmentData.reduce((acc, data) => {
                        acc[data.currentKey] = data.current.dataInfra;
                        acc[data.targetKey] = data.target.dataInfra;
                        return acc;
                    }, {})
                },
            ];
        } else {
            // Return single assessment radar data
            const { statistics } = selectedAssessment;
            return [
                {
                    subject: 'Hardware',
                    current: statistics.overallScores.hardware.current,
                    target: statistics.overallScores.hardware.target,
                    fullMark: 5,
                },
                {
                    subject: 'Network',
                    current: statistics.overallScores.network.current,
                    target: statistics.overallScores.network.target,
                    fullMark: 5,
                },
                {
                    subject: 'Software',
                    current: statistics.overallScores.software.current,
                    target: statistics.overallScores.software.target,
                    fullMark: 5,
                },
                {
                    subject: 'Data',
                    current: statistics.overallScores.dataInfrastructure.current,
                    target: statistics.overallScores.dataInfrastructure.target,
                    fullMark: 5,
                },
            ];
        }
    };

    const prepareBudgetData = () => {
        if (isComparisonMode) {
            return comparisonData.map((data, index) => ({
                // เพิ่ม index เข้าไปในชื่อเพื่อให้มี unique key
                name: `${data.assessment.assessment_name}_${index}`,
                displayName: data.assessment.assessment_name,  // เก็บชื่อจริงเพื่อแสดงผล
                hardware: parseFloat(data.budget.hardware_budget) || 0,
                network: parseFloat(data.budget.network_budget) || 0,
                software: parseFloat(data.budget.software_budget) || 0,
                dataInfra: parseFloat(data.budget.data_infrastructure_budget) || 0,
                total: (parseFloat(data.budget.hardware_budget) || 0) +
                    (parseFloat(data.budget.network_budget) || 0) +
                    (parseFloat(data.budget.software_budget) || 0) +
                    (parseFloat(data.budget.data_infrastructure_budget) || 0)
            }));
        } else {
            const { budget } = selectedAssessment;
            return [
                { name: 'Hardware', value: parseFloat(budget.hardware_budget) || 0 },
                { name: 'Network', value: parseFloat(budget.network_budget) || 0 },
                { name: 'Software', value: parseFloat(budget.software_budget) || 0 },
                { name: 'Data Infrastructure', value: parseFloat(budget.data_infrastructure_budget) || 0 }
            ];
        }
    };

    const prepareGapData = () => {
        if (isComparisonMode) {
            // ใช้ชื่อพร้อม index เป็น key เพื่อป้องกันปัญหาชื่อซ้ำกัน
            return comparisonData.map((data, index) => ({
                // เพิ่ม index เข้าไปในชื่อตรงนี้เพื่อให้มี unique key
                name: `${data.assessment.assessment_name}_${index}`,
                displayName: data.assessment.assessment_name,  // เก็บชื่อจริงเพื่อแสดงผล
                hardware: data.statistics.overallScores.hardware.gap,
                network: data.statistics.overallScores.network.gap,
                software: data.statistics.overallScores.software.gap,
                dataInfra: data.statistics.overallScores.dataInfrastructure.gap
            }));
        } else {
            const { statistics } = selectedAssessment;
            return [
                { name: 'Hardware', gap: statistics.overallScores.hardware.gap },
                { name: 'Network', gap: statistics.overallScores.network.gap },
                { name: 'Software', gap: statistics.overallScores.software.gap },
                { name: 'Data Infra', gap: statistics.overallScores.dataInfrastructure.gap }
            ];
        }
    };


    const preparePriorityData = () => {
        if (isComparisonMode) {
            // More complex priority comparison across assessments
            const sections = ['hardware', 'network', 'software', 'dataInfrastructure'];
            const priorities = ['H', 'M', 'L'];

            const result = sections.map(section => {
                const sectionName = section === 'dataInfrastructure' ? 'Data Infra' :
                    section.charAt(0).toUpperCase() + section.slice(1);

                const sectionData = { name: sectionName };

                comparisonData.forEach((data, index) => {
                    const assessmentName = data.assessment.assessment_name;
                    priorities.forEach(priority => {
                        const count = data.statistics.priorityDistribution[section][priority];
                        // เพิ่ม index เข้าไปในชื่อเพื่อให้มี unique key
                        const key = `${assessmentName}_${index}_${priority}`;
                        sectionData[key] = count;
                    });
                });

                return sectionData;
            });

            return result;
        } else {
            const { statistics } = selectedAssessment;
            const { priorityDistribution } = statistics;

            return Object.entries(priorityDistribution).map(([section, priorities]) => ({
                name: section === 'dataInfrastructure' ? 'Data Infra' : section.charAt(0).toUpperCase() + section.slice(1),
                High: priorities.H,
                Medium: priorities.M,
                Low: priorities.L
            }));
        }
    };

    const formatSectionName = (section) => {
        switch (section) {
            case 'hardware': return 'Hardware';
            case 'network': return 'Network';
            case 'software': return 'Software';
            case 'dataInfrastructure': return 'Data Infrastructure';
            default: return section;
        }
    };

    // Render comparison dashboard section
    const renderComparisonOverview = () => {
        if (!isComparisonMode || comparisonData.length === 0) return null;

        // Prepare data for comparison
        const radarData = prepareRadarData();
        const gapData = prepareGapData();
        const budgetData = prepareBudgetData();

        // Generate colors for each assessment
        const assessmentColors = comparisonData.map((_, index) => COLORS[index % COLORS.length]);

        // สร้างข้อมูลสำหรับใช้ในการสร้าง Radar components
        const assessmentRadarInfo = comparisonData.map((data, index) => ({
            name: data.assessment.assessment_name,
            currentKey: `${data.assessment.assessment_name}_current_${index}`,
            targetKey: `${data.assessment.assessment_name}_target_${index}`,
            color: assessmentColors[index]
        }));

        return (
            <div>
                <div className="bg-white p-6 rounded-lg shadow mb-6">
                    <h3 className="text-xl font-semibold mb-4">Comparison Overview</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        {comparisonData.map((data, index) => (
                            <div key={index} className="border p-4 rounded" style={{ borderLeftColor: assessmentColors[index], borderLeftWidth: '4px' }}>
                                <h4 className="font-semibold">{data.assessment.assessment_name}</h4>
                                <p className="text-sm">{data.assessment.company_name}</p>
                                <p className="text-sm">Date: {new Date(data.assessment.assessment_date).toLocaleDateString()}</p>
                            </div>
                        ))}
                    </div>

                    <div className="mb-8">
                        <h4 className="text-lg font-semibold mb-2">Digital Transformation Readiness Comparison</h4>
                        <ResponsiveContainer width="100%" height={500}>
                            <RadarChart outerRadius={150} data={radarData}>
                                <PolarGrid />
                                <PolarAngleAxis dataKey="subject" />
                                <PolarRadiusAxis angle={30} domain={[0, 5]} />

                                {/* สร้าง Radar สำหรับ Current Status ของแต่ละ assessment */}
                                {assessmentRadarInfo.map(info => (
                                    <Radar
                                        key={info.currentKey}
                                        name={`${info.name} (Current)`}
                                        dataKey={info.currentKey}
                                        stroke={info.color}
                                        fill={info.color}
                                        fillOpacity={0.6}
                                    />
                                ))}

                                {/* สร้าง Radar สำหรับ Target ของแต่ละ assessment */}
                                {assessmentRadarInfo.map(info => (
                                    <Radar
                                        key={info.targetKey}
                                        name={`${info.name} (Target)`}
                                        dataKey={info.targetKey}
                                        stroke={info.color}
                                        fill="none"
                                        strokeWidth={2}
                                        strokeDasharray="5 5" // ทำให้เส้นเป็นเส้นประ
                                    />
                                ))}

                                <Legend />
                                <Tooltip />
                            </RadarChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="mb-8">
                        <h4 className="text-lg font-semibold mb-2">Technology Gaps Comparison</h4>
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart data={gapData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="displayName"  // ใช้ displayName แทน name เพื่อแสดงผลชื่อที่อ่านได้
                                    // หรือสามารถใช้ formatter function ถ้า displayName ไม่มี
                                    tickFormatter={(value) => {
                                        // ตัดส่วน _index ออกเพื่อแสดงเฉพาะชื่อ
                                        return value.split('_')[0];
                                    }}
                                />
                                <YAxis />
                                <Tooltip
                                    // แปลงการแสดงผลชื่อใน tooltip ด้วย
                                    formatter={(value, name, props) => {
                                        return [value, name];
                                    }}
                                    labelFormatter={(label) => {
                                        // ตัดส่วน _index ออกเพื่อแสดงเฉพาะชื่อใน tooltip
                                        return label.split('_')[0];
                                    }}
                                />
                                <Legend />
                                <Bar dataKey="hardware" name="Hardware Gap" fill="#0088FE" />
                                <Bar dataKey="network" name="Network Gap" fill="#00C49F" />
                                <Bar dataKey="software" name="Software Gap" fill="#FFBB28" />
                                <Bar dataKey="dataInfra" name="Data Infra Gap" fill="#FF8042" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    <div>
                        <h4 className="text-lg font-semibold mb-2">Budget Comparison</h4>
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart data={budgetData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="displayName"  // ใช้ displayName แทน name
                                    // หรือสามารถใช้ formatter function ถ้า displayName ไม่มี
                                    tickFormatter={(value) => {
                                        if (value.includes('_')) {
                                            return value.split('_')[0];
                                        }
                                        return value;
                                    }}
                                />
                                <YAxis tickFormatter={(value) => value.toLocaleString()} />
                                <Tooltip
                                    formatter={(value) => value.toLocaleString() + ' บาท'}
                                    labelFormatter={(label) => {
                                        if (label.includes('_')) {
                                            return label.split('_')[0];
                                        }
                                        return label;
                                    }}
                                />
                                <Legend />
                                <Bar dataKey="total" name="Total Budget" fill="#8884d8" />
                                <Bar dataKey="hardware" name="Hardware" fill="#0088FE" />
                                <Bar dataKey="network" name="Network" fill="#00C49F" />
                                <Bar dataKey="software" name="Software" fill="#FFBB28" />
                                <Bar dataKey="dataInfra" name="Data Infrastructure" fill="#FF8042" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-4">Detailed Assessment Comparison</h3>

                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="py-2 px-3 border-b">Category</th>
                                    {comparisonData.map((data, index) => (
                                        <th key={index} className="py-2 px-3border-b text-center" style={{ backgroundColor: `${assessmentColors[index]}20` }}>
                                            {data.assessment.assessment_name}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="py-2 px-3 border-b font-semibold">Company</td>
                                    {comparisonData.map((data, index) => (
                                        <td key={index} className="py-2 px-3 border-b text-center">
                                            {data.assessment.company_name}
                                        </td>
                                    ))}
                                </tr>
                                <tr>
                                    <td className="py-2 px-3 border-b font-semibold">Assessment Date</td>
                                    {comparisonData.map((data, index) => (
                                        <td key={index} className="py-2 px-3 border-b text-center">
                                            {new Date(data.assessment.assessment_date).toLocaleDateString()}
                                        </td>
                                    ))}
                                </tr><tr>
                                    <td className="py-2 px-3 border-b font-semibold">Total Budget</td>
                                    {comparisonData.map((data, index) => {
                                        const totalBudget =
                                            (parseFloat(data.budget.hardware_budget) || 0) +
                                            (parseFloat(data.budget.network_budget) || 0) +
                                            (parseFloat(data.budget.software_budget) || 0) +
                                            (parseFloat(data.budget.data_infrastructure_budget) || 0);

                                        return (
                                            <td key={index} className="py-2 px-3 border-b text-center">
                                                {totalBudget.toLocaleString()} บาท
                                            </td>
                                        );
                                    })}
                                </tr>
                                <tr className="bg-blue-50">
                                    <td className="py-2 px-3 border-b font-semibold">Hardware Score</td>
                                    {comparisonData.map((data, index) => (
                                        <td key={index} className="py-2 px-3 border-b text-center">
                                            {data.statistics.overallScores.hardware.current}/5
                                        </td>
                                    ))}
                                </tr>
                                <tr className="bg-blue-50">
                                    <td className="py-2 px-3 border-b font-semibold">Hardware Gap</td>
                                    {comparisonData.map((data, index) => (
                                        <td key={index} className="py-2 px-3 border-b text-center">
                                            {data.statistics.overallScores.hardware.gap}
                                        </td>
                                    ))}
                                </tr>
                                <tr className="bg-green-50">
                                    <td className="py-2 px-3 border-b font-semibold">Network Score</td>
                                    {comparisonData.map((data, index) => (
                                        <td key={index} className="py-2 px-3 border-b text-center">
                                            {data.statistics.overallScores.network.current}/5
                                        </td>
                                    ))}
                                </tr>
                                <tr className="bg-green-50">
                                    <td className="py-2 px-3 border-b font-semibold">Network Gap</td>
                                    {comparisonData.map((data, index) => (
                                        <td key={index} className="py-2 px-3 border-b text-center">
                                            {data.statistics.overallScores.network.gap}
                                        </td>
                                    ))}
                                </tr>
                                <tr className="bg-yellow-50">
                                    <td className="py-2 px-3 border-b font-semibold">Software Score</td>
                                    {comparisonData.map((data, index) => (
                                        <td key={index} className="py-2 px-3 border-b text-center">
                                            {data.statistics.overallScores.software.current}/5
                                        </td>
                                    ))}
                                </tr>
                                <tr className="bg-yellow-50">
                                    <td className="py-2 px-3 border-b font-semibold">Software Gap</td>
                                    {comparisonData.map((data, index) => (
                                        <td key={index} className="py-2 px-3 border-b text-center">
                                            {data.statistics.overallScores.software.gap}
                                        </td>
                                    ))}
                                </tr>
                                <tr className="bg-orange-50">
                                    <td className="py-2 px-3 border-b font-semibold">Data Infrastructure Score</td>
                                    {comparisonData.map((data, index) => (
                                        <td key={index} className="py-2 px-3 border-b text-center">
                                            {data.statistics.overallScores.dataInfrastructure.current}/5
                                        </td>
                                    ))}
                                </tr>
                                <tr className="bg-orange-50">
                                    <td className="py-2 px-3 border-b font-semibold">Data Infrastructure Gap</td>
                                    {comparisonData.map((data, index) => (
                                        <td key={index} className="py-2 px-3 border-b text-center">
                                            {data.statistics.overallScores.dataInfrastructure.gap}
                                        </td>
                                    ))}
                                </tr>
                                <tr>
                                    <td className="py-2 px-3 border-b font-semibold">High Priority Items</td>
                                    {comparisonData.map((data, index) => {
                                        const highCount = data.statistics.gaps.filter(g => g.priority === 'H').length;

                                        return (
                                            <td key={index} className="py-2 px-3 border-b text-center">
                                                <span className="inline-block bg-red-500 text-white rounded-full px-3 py-1">
                                                    {highCount}
                                                </span>
                                            </td>
                                        );
                                    })}
                                </tr>
                                <tr>
                                    <td className="py-2 px-3 border-b font-semibold">Medium Priority Items</td>
                                    {comparisonData.map((data, index) => {
                                        const mediumCount = data.statistics.gaps.filter(g => g.priority === 'M').length;

                                        return (
                                            <td key={index} className="py-2 px-3 border-b text-center">
                                                <span className="inline-block bg-yellow-500 text-white rounded-full px-3 py-1">
                                                    {mediumCount}
                                                </span>
                                            </td>
                                        );
                                    })}
                                </tr>
                                <tr>
                                    <td className="py-2 px-3 border-b font-semibold">Low Priority Items</td>
                                    {comparisonData.map((data, index) => {
                                        const lowCount = data.statistics.gaps.filter(g => g.priority === 'L').length;

                                        return (
                                            <td key={index} className="py-2 px-3 border-b text-center">
                                                <span className="inline-block bg-green-500 text-white rounded-full px-3 py-1">
                                                    {lowCount}
                                                </span>
                                            </td>
                                        );
                                    })}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    // Render dashboard sections
    const renderOverview = () => {
        if (isComparisonMode) {
            return renderComparisonOverview();
        }

        if (!selectedAssessment) return null;

        const { assessment, statistics, budget } = selectedAssessment;
        const totalBudget = parseFloat(budget.hardware_budget || 0) +
            parseFloat(budget.network_budget || 0) +
            parseFloat(budget.software_budget || 0) +
            parseFloat(budget.data_infrastructure_budget || 0);

        const radarData = prepareRadarData();
        const gapData = prepareGapData();

        // Custom tooltip for radar chart
        const CustomRadarTooltip = ({ active, payload }) => {
            if (active && payload && payload.length) {
                const data = payload[0].payload;
                return (
                    <div className="bg-white p-3 border rounded shadow-lg">
                        <p className="font-bold text-lg mb-1">{data.subject}</p>
                        <p className="text-blue-600"><span className="font-semibold">Current Status:</span> {data.current}/5</p>
                        <p className="text-green-600"><span className="font-semibold">Target Level:</span> {data.target}/5</p>
                        <p className="text-orange-600"><span className="font-semibold">Gap:</span> {(data.target - data.current).toFixed(1)}</p>
                        <p className="text-purple-600"><span className="font-semibold">Readiness:</span> {((data.current / data.target) * 100).toFixed(1)}%</p>
                    </div>
                );
            }
            return null;
        };

        return (
            <div>
                {renderSuccessMessage()}
                {renderErrorMessage()}
                {renderDeleteConfirmModal()}

                <div className="bg-white p-6 rounded-lg shadow mb-6">
                    <h3 className="text-xl font-semibold mb-4">Assessment Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <p><span className="font-semibold">Assessment Name:</span> {assessment.assessment_name}</p>
                            <p><span className="font-semibold">Company:</span> {assessment.company_name}</p>
                            <p><span className="font-semibold">Assessor:</span> {assessment.assessor_name}</p>
                        </div>
                        <div>
                            <p><span className="font-semibold">Position:</span> {assessment.position}</p>
                            <p><span className="font-semibold">Date:</span> {new Date(assessment.assessment_date).toLocaleDateString()}</p>
                            <p><span className="font-semibold">Total Budget:</span> {totalBudget.toLocaleString()} บาท</p>
                        </div>
                    </div>
                    <div className="flex justify-end mt-4">
                        <button
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 mr-2"
                            onClick={() => {
                                onEditAssessment(selectedAssessmentId);
                            }}
                        >
                            Edit Assessment
                        </button>
                        <button
                            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                            onClick={() => {
                                setAssessmentToDelete(selectedAssessmentId);
                                setShowDeleteConfirm(true);
                            }}
                        >
                            Delete Assessment
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="bg-white p-6 rounded-lg shadow">
                        <h3 className="text-lg font-semibold mb-4">Digital Transformation Readiness</h3>
                        <p className="text-sm text-gray-600 italic mb-2">เมาส์ชี้ที่จุดเพื่อดูรายละเอียด</p>
                        <ResponsiveContainer width="100%" height={300}>
                            <RadarChart outerRadius={90} data={radarData}>
                                <PolarGrid />
                                <PolarAngleAxis dataKey="subject" />
                                <PolarRadiusAxis angle={30} domain={[0, 5]} />
                                <Radar name="Current Status" dataKey="current" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                                <Radar name="Target" dataKey="target" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.6} />
                                <Legend />
                                <Tooltip content={<CustomRadarTooltip />} />
                            </RadarChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow">
                        <h3 className="text-lg font-semibold mb-4">Technology Gaps</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={gapData} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" domain={[0, 5]} />
                                <YAxis dataKey="name" type="category" />
                                <Tooltip formatter={(value) => [`${value.toFixed(2)}`, 'Gap']} />
                                <Legend />
                                <Bar dataKey="gap" fill="#ff8042" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white p-6 rounded-lg shadow">
                        <h3 className="text-lg font-semibold mb-4">Budget Allocation</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={prepareBudgetData()}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={true}
                                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {prepareBudgetData().map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(value) => value.toLocaleString() + ' บาท'} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow">
                        <h3 className="text-lg font-semibold mb-4">Priority Distribution</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={preparePriorityData()}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="High" stackId="a" fill={PRIORITY_COLORS.H} />
                                <Bar dataKey="Medium" stackId="a" fill={PRIORITY_COLORS.M} />
                                <Bar dataKey="Low" stackId="a" fill={PRIORITY_COLORS.L} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        );
    };

    const renderHardwareSection = () => {
        if (isComparisonMode) {
            // Render hardware comparison
            const hardwareComparison = comparisonData.map(data => {
                const hardwareItems = data.items.filter(item => item.section === 'hardware');
                const hardwareStats = data.statistics.aggregated.filter(stat => stat.section === 'hardware');

                return {
                    name: data.assessment.assessment_name,
                    items: hardwareItems,
                    stats: hardwareStats,
                    scores: data.statistics.overallScores.hardware
                };
            });

            return (
                <div>
                    <div className="bg-white p-6 rounded-lg shadow mb-6">
                        <h3 className="text-xl font-semibold mb-4">Hardware Comparison</h3>

                        <div className="overflow-x-auto mb-6">
                            <table className="min-w-full bg-white">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="py-2 px-3 border-b">Assessment</th>
                                        <th className="py-2 px-3 border-b text-center">Current Score</th>
                                        <th className="py-2 px-3 border-b text-center">Target Score</th>
                                        <th className="py-2 px-3 border-b text-center">Gap</th>
                                        <th className="py-2 px-3 border-b text-center">Readiness %</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {hardwareComparison.map((comparison, index) => (
                                        <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                                            <td className="py-2 px-3 border-b font-semibold">{comparison.name}</td>
                                            <td className="py-2 px-3 border-b text-center">{comparison.scores.current}/5</td>
                                            <td className="py-2 px-3 border-b text-center">{comparison.scores.target}/5</td>
                                            <td className="py-2 px-3 border-b text-center">{comparison.scores.gap}</td>
                                            <td className="py-2 px-3 border-b text-center">{comparison.scores.percentage}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <h4 className="text-lg font-semibold mb-2">Hardware Category Comparison</h4>
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart
                                data={
                                    // Get all unique categories
                                    [...new Set(comparisonData.flatMap(data =>
                                        data.statistics.aggregated
                                            .filter(stat => stat.section === 'hardware')
                                            .map(stat => stat.category)
                                    ))]
                                        // For each category, create a data point with scores from each assessment
                                        .map(category => {
                                            const dataPoint = { name: category };

                                            comparisonData.forEach((data, dataIndex) => {
                                                const statForCategory = data.statistics.aggregated
                                                    .find(stat => stat.section === 'hardware' && stat.category === category);

                                                // ใช้ unique key โดยเพิ่ม index
                                                const uniqueKey = `${data.assessment.assessment_name}_${dataIndex}`;

                                                if (statForCategory) {
                                                    dataPoint[uniqueKey] = parseFloat(statForCategory.avg_current_status);
                                                } else {
                                                    dataPoint[uniqueKey] = 0;
                                                }
                                            });

                                            return dataPoint;
                                        })
                                }
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis domain={[0, 5]} />
                                <Tooltip />
                                <Legend />

                                {comparisonData.map((data, index) => {
                                    // สร้าง unique key และชื่อที่จะแสดงในกราฟ
                                    const uniqueKey = `${data.assessment.assessment_name}_${index}`;
                                    const displayName = data.assessment.assessment_name;

                                    return (
                                        <Bar
                                            key={uniqueKey}
                                            dataKey={uniqueKey}
                                            name={displayName}
                                            fill={COLORS[index % COLORS.length]}
                                        />
                                    );
                                })}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            );
        }

        if (!selectedAssessment) return null;

        const { items, statistics } = selectedAssessment;

        // Filter items for hardware section
        const hardwareItems = items.filter(item => item.section === 'hardware');

        // Get stats for hardware
        const hardwareStats = statistics.aggregated.filter(stat => stat.section === 'hardware');

        // Prepare chart data
        const categoryScoreData = hardwareStats.map(stat => ({
            name: stat.category,
            current: parseFloat(stat.avg_current_status),
            target: parseFloat(stat.avg_target),
            gap: parseFloat(stat.avg_target) - parseFloat(stat.avg_current_status)
        }));

        return (
            <div>
                {renderSuccessMessage()}
                {renderErrorMessage()}
                {renderDeleteConfirmModal()}

                <div className="bg-white p-6 rounded-lg shadow mb-6">
                    <h3 className="text-xl font-semibold mb-4">Hardware Overview</h3>

                    <div className="border rounded p-4 mb-6">
                        <h4 className="text-lg font-semibold mb-2">Hardware</h4>
                        <p><span className="font-semibold">Current Score:</span> {statistics.overallScores.hardware.current}/5</p>
                        <p><span className="font-semibold">Target Score:</span> {statistics.overallScores.hardware.target}/5</p>
                        <p><span className="font-semibold">Gap:</span> {statistics.overallScores.hardware.gap}</p>
                        <p><span className="font-semibold">Readiness:</span> {statistics.overallScores.hardware.percentage}%</p>
                    </div>

                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={categoryScoreData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis domain={[0, 5]} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="current" name="Current Status" fill="#0088FE" />
                            <Bar dataKey="target" name="Target" fill="#82ca9d" />
                            <Bar dataKey="gap" name="Gap" fill="#ff8042" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-4">Hardware Items</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="py-2 px-3 border-b text-left">Category</th>
                                    <th className="py-2 px-3 border-b text-left">Item</th>
                                    <th className="py-2 px-3 border-b text-left">Details</th>
                                    <th className="py-2 px-3 border-b text-center">Status</th>
                                    <th className="py-2 px-3 border-b text-center">Target</th>
                                    <th className="py-2 px-3 border-b text-center">Gap</th>
                                    <th className="py-2 px-3 border-b text-center">Priority</th>
                                    <th className="py-2 px-3 border-b text-center">Age (Months)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {hardwareItems.map((item, index) => (
                                    <tr key={`hardware-${index}`} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                                        <td className="py-2 px-3 border-b">{item.category}</td>
                                        <td className="py-2 px-3 border-b">{item.item}</td>
                                        <td className="py-2 px-3 border-b">{item.details}</td>
                                        <td className="py-2 px-3 border-b text-center">{item.current_status}</td>
                                        <td className="py-2 px-3 border-b text-center">{item.target}</td>
                                        <td className="py-2 px-3 border-b text-center">{item.target - item.current_status}</td>
                                        <td className="py-2 px-3 border-b text-center">
                                            <span className={`inline-block rounded-full w-6 h-6 text-white text-xs flex items-center justify-center ${item.priority === 'H' ? 'bg-red-500' :
                                                item.priority === 'M' ? 'bg-yellow-500' :
                                                    item.priority === 'L' ? 'bg-green-500' : 'bg-gray-400'
                                                }`}>
                                                {item.priority || '-'}
                                            </span>
                                        </td>
                                        <td className="py-2 px-3 border-b text-center">{item.age || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    const renderNetworkSection = () => {
        if (isComparisonMode) {
            // Render network comparison
            const networkComparison = comparisonData.map(data => {
                const networkItems = data.items.filter(item => item.section === 'network');
                const networkStats = data.statistics.aggregated.filter(stat => stat.section === 'network');

                return {
                    name: data.assessment.assessment_name,
                    items: networkItems,
                    stats: networkStats,
                    scores: data.statistics.overallScores.network
                };
            });

            return (
                <div>
                    <div className="bg-white p-6 rounded-lg shadow mb-6">
                        <h3 className="text-xl font-semibold mb-4">Network Comparison</h3>

                        <div className="overflow-x-auto mb-6">
                            <table className="min-w-full bg-white">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="py-2 px-3 border-b">Assessment</th>
                                        <th className="py-2 px-3 border-b text-center">Current Score</th>
                                        <th className="py-2 px-3 border-b text-center">Target Score</th>
                                        <th className="py-2 px-3 border-b text-center">Gap</th>
                                        <th className="py-2 px-3 border-b text-center">Readiness %</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {networkComparison.map((comparison, index) => (
                                        <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                                            <td className="py-2 px-3 border-b font-semibold">{comparison.name}</td>
                                            <td className="py-2 px-3 border-b text-center">{comparison.scores.current}/5</td>
                                            <td className="py-2 px-3 border-b text-center">{comparison.scores.target}/5</td>
                                            <td className="py-2 px-3 border-b text-center">{comparison.scores.gap}</td>
                                            <td className="py-2 px-3 border-b text-center">{comparison.scores.percentage}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <h4 className="text-lg font-semibold mb-2">Network Category Comparison</h4>
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart
                                data={
                                    [...new Set(comparisonData.flatMap(data =>
                                        data.statistics.aggregated
                                            .filter(stat => stat.section === 'network')
                                            .map(stat => stat.category)
                                    ))].map(category => {
                                        const dataPoint = { name: category };

                                        comparisonData.forEach((data, dataIndex) => {
                                            const statForCategory = data.statistics.aggregated
                                                .find(stat => stat.section === 'network' && stat.category === category);

                                            // ใช้ unique key โดยเพิ่ม index
                                            const uniqueKey = `${data.assessment.assessment_name}_${dataIndex}`;

                                            if (statForCategory) {
                                                dataPoint[uniqueKey] = parseFloat(statForCategory.avg_current_status);
                                            } else {
                                                dataPoint[uniqueKey] = 0;
                                            }
                                        });

                                        return dataPoint;
                                    })
                                }
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis domain={[0, 5]} />
                                <Tooltip />
                                <Legend />

                                {comparisonData.map((data, index) => {
                                    // สร้าง unique key และชื่อที่จะแสดงในกราฟ
                                    const uniqueKey = `${data.assessment.assessment_name}_${index}`;
                                    const displayName = data.assessment.assessment_name;

                                    return (
                                        <Bar
                                            key={uniqueKey}
                                            dataKey={uniqueKey}
                                            name={displayName}
                                            fill={COLORS[index % COLORS.length]}
                                        />
                                    );
                                })}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            );
        }

        if (!selectedAssessment) return null;

        const { items, statistics } = selectedAssessment;

        // Filter items for network section
        const networkItems = items.filter(item => item.section === 'network');

        // Get stats for network
        const networkStats = statistics.aggregated.filter(stat => stat.section === 'network');

        // Prepare chart data
        const categoryScoreData = networkStats.map(stat => ({
            name: stat.category,
            current: parseFloat(stat.avg_current_status),
            target: parseFloat(stat.avg_target),
            gap: parseFloat(stat.avg_target) - parseFloat(stat.avg_current_status)
        }));

        return (
            <div>
                {renderSuccessMessage()}
                {renderErrorMessage()}
                {renderDeleteConfirmModal()}

                <div className="bg-white p-6 rounded-lg shadow mb-6">
                    <h3 className="text-xl font-semibold mb-4">Network Overview</h3>

                    <div className="border rounded p-4 mb-6">
                        <h4 className="text-lg font-semibold mb-2">Network</h4>
                        <p><span className="font-semibold">Current Score:</span> {statistics.overallScores.network.current}/5</p>
                        <p><span className="font-semibold">Target Score:</span> {statistics.overallScores.network.target}/5</p>
                        <p><span className="font-semibold">Gap:</span> {statistics.overallScores.network.gap}</p>
                        <p><span className="font-semibold">Readiness:</span> {statistics.overallScores.network.percentage}%</p>
                    </div>

                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={categoryScoreData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis domain={[0, 5]} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="current" name="Current Status" fill="#00C49F" />
                            <Bar dataKey="target" name="Target" fill="#82ca9d" />
                            <Bar dataKey="gap" name="Gap" fill="#ff8042" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-4">Network Items</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="py-2 px-3 border-b text-left">Category</th>
                                    <th className="py-2 px-3 border-b text-left">Item</th>
                                    <th className="py-2 px-3 border-b text-left">Details</th>
                                    <th className="py-2 px-3 border-b text-center">Status</th>
                                    <th className="py-2 px-3 border-b text-center">Target</th>
                                    <th className="py-2 px-3 border-b text-center">Gap</th>
                                    <th className="py-2 px-3 border-b text-center">Priority</th>
                                    <th className="py-2 px-3 border-b text-left">Responsible</th>
                                </tr>
                            </thead>
                            <tbody>
                                {networkItems.map((item, index) => (
                                    <tr key={`network-${index}`} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                                        <td className="py-2 px-3 border-b">{item.category}</td>
                                        <td className="py-2 px-3 border-b">{item.item}</td>
                                        <td className="py-2 px-3 border-b">{item.details}</td>
                                        <td className="py-2 px-3 border-b text-center">{item.current_status}</td>
                                        <td className="py-2 px-3 border-b text-center">{item.target}</td>
                                        <td className="py-2 px-3 border-b text-center">{item.target - item.current_status}</td>
                                        <td className="py-2 px-3 border-b text-center">
                                            <span className={`inline-block rounded-full w-6 h-6 text-white text-xs flex items-center justify-center ${item.priority === 'H' ? 'bg-red-500' :
                                                item.priority === 'M' ? 'bg-yellow-500' :
                                                    item.priority === 'L' ? 'bg-green-500' : 'bg-gray-400'
                                                }`}>
                                                {item.priority || '-'}
                                            </span>
                                        </td>
                                        <td className="py-2 px-3 border-b">{item.responsible}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    const renderSoftwareSection = () => {
        if (isComparisonMode) {
            // Render software comparison
            const softwareComparison = comparisonData.map(data => {
                const softwareItems = data.items.filter(item => item.section === 'software');
                const softwareStats = data.statistics.aggregated.filter(stat => stat.section === 'software');

                return {
                    name: data.assessment.assessment_name,
                    items: softwareItems,
                    stats: softwareStats,
                    scores: data.statistics.overallScores.software
                };
            });

            return (
                <div>
                    <div className="bg-white p-6 rounded-lg shadow mb-6">
                        <h3 className="text-xl font-semibold mb-4">Software Comparison</h3>

                        <div className="overflow-x-auto mb-6">
                            <table className="min-w-full bg-white">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="py-2 px-3 border-b">Assessment</th>
                                        <th className="py-2 px-3 border-b text-center">Current Score</th>
                                        <th className="py-2 px-3 border-b text-center">Target Score</th>
                                        <th className="py-2 px-3 border-b text-center">Gap</th>
                                        <th className="py-2 px-3 border-b text-center">Readiness %</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {softwareComparison.map((comparison, index) => (
                                        <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                                            <td className="py-2 px-3 border-b font-semibold">{comparison.name}</td>
                                            <td className="py-2 px-3 border-b text-center">{comparison.scores.current}/5</td>
                                            <td className="py-2 px-3 border-b text-center">{comparison.scores.target}/5</td>
                                            <td className="py-2 px-3 border-b text-center">{comparison.scores.gap}</td>
                                            <td className="py-2 px-3 border-b text-center">{comparison.scores.percentage}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <h4 className="text-lg font-semibold mb-2">Software Category Comparison</h4>
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart
                                data={
                                    [...new Set(comparisonData.flatMap(data =>
                                        data.statistics.aggregated
                                            .filter(stat => stat.section === 'software')
                                            .map(stat => stat.category)
                                    ))].map(category => {
                                        const dataPoint = { name: category };

                                        comparisonData.forEach((data, dataIndex) => {
                                            const statForCategory = data.statistics.aggregated
                                                .find(stat => stat.section === 'software' && stat.category === category);

                                            // ใช้ unique key โดยเพิ่ม index
                                            const uniqueKey = `${data.assessment.assessment_name}_${dataIndex}`;

                                            if (statForCategory) {
                                                dataPoint[uniqueKey] = parseFloat(statForCategory.avg_current_status);
                                            } else {
                                                dataPoint[uniqueKey] = 0;
                                            }
                                        });

                                        return dataPoint;
                                    })
                                }
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={100} />
                                <YAxis domain={[0, 5]} />
                                <Tooltip />
                                <Legend />

                                {comparisonData.map((data, index) => {
                                    // สร้าง unique key และชื่อที่จะแสดงในกราฟ
                                    const uniqueKey = `${data.assessment.assessment_name}_${index}`;
                                    const displayName = data.assessment.assessment_name;

                                    return (
                                        <Bar
                                            key={uniqueKey}
                                            dataKey={uniqueKey}
                                            name={displayName}
                                            fill={COLORS[index % COLORS.length]}
                                        />
                                    );
                                })}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            );
        }

        if (!selectedAssessment) return null;

        const { items, statistics } = selectedAssessment;

        // Filter items for software section
        const softwareItems = items.filter(item => item.section === 'software');

        // Get stats for software categories
        const softwareStats = statistics.aggregated.filter(stat => stat.section === 'software');

        // Prepare chart data for software categories
        const categoryScoreData = softwareStats.map(stat => ({
            name: stat.category,
            current: parseFloat(stat.avg_current_status),
            target: parseFloat(stat.avg_target),
            gap: parseFloat(stat.avg_target) - parseFloat(stat.avg_current_status)
        }));

        // Group software items by category
        const softwareByCategory = softwareItems.reduce((acc, item) => {
            if (!acc[item.category]) {
                acc[item.category] = [];
            }
            acc[item.category].push(item);
            return acc;
        }, {});

        return (
            <div>
                {renderSuccessMessage()}
                {renderErrorMessage()}
                {renderDeleteConfirmModal()}

                <div className="bg-white p-6 rounded-lg shadow mb-6">
                    <h3 className="text-xl font-semibold mb-4">Software Overview</h3>

                    <div className="border rounded p-4 mb-6">
                        <h4 className="text-lg font-semibold mb-2">Software</h4>
                        <p><span className="font-semibold">Current Score:</span> {statistics.overallScores.software.current}/5</p>
                        <p><span className="font-semibold">Target Score:</span> {statistics.overallScores.software.target}/5</p>
                        <p><span className="font-semibold">Gap:</span> {statistics.overallScores.software.gap}</p>
                        <p><span className="font-semibold">Readiness:</span> {statistics.overallScores.software.percentage}%</p>
                    </div>

                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={categoryScoreData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={100} />
                            <YAxis domain={[0, 5]} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="current" name="Current Status" fill="#8884d8" />
                            <Bar dataKey="target" name="Target" fill="#82ca9d" />
                            <Bar dataKey="gap" name="Gap" fill="#ff8042" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-4">Software by Category</h3>

                    {Object.entries(softwareByCategory).map(([category, items]) => (
                        <div key={category} className="mb-6">
                            <h4 className="text-md font-semibold mb-2 bg-gray-100 p-2">{category}</h4>
                            <div className="overflow-x-auto">
                                <table className="min-w-full bg-white">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="py-2 px-3 border-b text-left">Item</th>
                                            <th className="py-2 px-3 border-b text-left">Details</th>
                                            <th className="py-2 px-3 border-b text-center">Version</th>
                                            <th className="py-2 px-3 border-b text-center">Licenses</th>
                                            <th className="py-2 px-3 border-b text-center">Status</th>
                                            <th className="py-2 px-3 border-b text-center">Target</th>
                                            <th className="py-2 px-3 border-b text-center">Priority</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((item, index) => (
                                            <tr key={`software-${index}`} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                                                <td className="py-2 px-3 border-b">{item.item}</td>
                                                <td className="py-2 px-3 border-b">{item.details}</td>
                                                <td className="py-2 px-3 border-b text-center">{item.version}</td>
                                                <td className="py-2 px-3 border-b text-center">{item.licenses}</td>
                                                <td className="py-2 px-3 border-b text-center">{item.current_status}</td>
                                                <td className="py-2 px-3 border-b text-center">{item.target}</td>
                                                <td className="py-2 px-3 border-b text-center">
                                                    <span className={`inline-block rounded-full w-6 h-6 text-white text-xs flex items-center justify-center ${item.priority === 'H' ? 'bg-red-500' :
                                                        item.priority === 'M' ? 'bg-yellow-500' :
                                                            item.priority === 'L' ? 'bg-green-500' : 'bg-gray-400'
                                                        }`}>
                                                        {item.priority || '-'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderDataInfrastructureSection = () => {
        if (isComparisonMode) {
            // Render data infrastructure comparison
            const dataInfraComparison = comparisonData.map(data => {
                const dataItems = data.items.filter(item => item.section === 'dataInfrastructure');
                const dataStats = data.statistics.aggregated.filter(stat => stat.section === 'dataInfrastructure');

                return {
                    name: data.assessment.assessment_name,
                    items: dataItems,
                    stats: dataStats,
                    scores: data.statistics.overallScores.dataInfrastructure
                };
            });

            return (
                <div>
                    <div className="bg-white p-6 rounded-lg shadow mb-6">
                        <h3 className="text-xl font-semibold mb-4">Data Infrastructure Comparison</h3>

                        <div className="overflow-x-auto mb-6">
                            <table className="min-w-full bg-white">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="py-2 px-3 border-b">Assessment</th>
                                        <th className="py-2 px-3 border-b text-center">Current Score</th>
                                        <th className="py-2 px-3 border-b text-center">Target Score</th>
                                        <th className="py-2 px-3 border-b text-center">Gap</th>
                                        <th className="py-2 px-3 border-b text-center">Readiness %</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dataInfraComparison.map((comparison, index) => (
                                        <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                                            <td className="py-2 px-3 border-b font-semibold">{comparison.name}</td>
                                            <td className="py-2 px-3 border-b text-center">{comparison.scores.current}/5</td>
                                            <td className="py-2 px-3 border-b text-center">{comparison.scores.target}/5</td>
                                            <td className="py-2 px-3 border-b text-center">{comparison.scores.gap}</td>
                                            <td className="py-2 px-3 border-b text-center">{comparison.scores.percentage}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <h4 className="text-lg font-semibold mb-2">Data Infrastructure Category Comparison</h4>
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart
                                data={
                                    [...new Set(comparisonData.flatMap(data =>
                                        data.statistics.aggregated
                                            .filter(stat => stat.section === 'dataInfrastructure')
                                            .map(stat => stat.category)
                                    ))].map(category => {
                                        const dataPoint = { name: category };

                                        comparisonData.forEach((data, dataIndex) => {
                                            const statForCategory = data.statistics.aggregated
                                                .find(stat => stat.section === 'dataInfrastructure' && stat.category === category);

                                            // ใช้ unique key โดยเพิ่ม index
                                            const uniqueKey = `${data.assessment.assessment_name}_${dataIndex}`;

                                            if (statForCategory) {
                                                dataPoint[uniqueKey] = parseFloat(statForCategory.avg_current_status);
                                            } else {
                                                dataPoint[uniqueKey] = 0;
                                            }
                                        });

                                        return dataPoint;
                                    })
                                }
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis domain={[0, 5]} />
                                <Tooltip />
                                <Legend />

                                {comparisonData.map((data, index) => {
                                    // สร้าง unique key และชื่อที่จะแสดงในกราฟ
                                    const uniqueKey = `${data.assessment.assessment_name}_${index}`;
                                    const displayName = data.assessment.assessment_name;

                                    return (
                                        <Bar
                                            key={uniqueKey}
                                            dataKey={uniqueKey}
                                            name={displayName}
                                            fill={COLORS[index % COLORS.length]}
                                        />
                                    );
                                })}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            );
        }

        if (!selectedAssessment) return null;

        const { items, statistics } = selectedAssessment;

        // Filter items for data infrastructure section
        const dataItems = items.filter(item => item.section === 'dataInfrastructure');

        // Get stats for data infrastructure categories
        const dataStats = statistics.aggregated.filter(stat => stat.section === 'dataInfrastructure');

        // Prepare chart data
        const categoryScoreData = dataStats.map(stat => ({
            name: stat.category,
            current: parseFloat(stat.avg_current_status),
            target: parseFloat(stat.avg_target),
            gap: parseFloat(stat.avg_target) - parseFloat(stat.avg_current_status)
        }));

        return (
            <div>
                {renderSuccessMessage()}
                {renderErrorMessage()}
                {renderDeleteConfirmModal()}

                <div className="bg-white p-6 rounded-lg shadow mb-6">
                    <h3 className="text-xl font-semibold mb-4">Data Infrastructure Overview</h3>

                    <div className="border rounded p-4 mb-6">
                        <h4 className="text-lg font-semibold mb-2">Data Infrastructure</h4>
                        <p><span className="font-semibold">Current Score:</span> {statistics.overallScores.dataInfrastructure.current}/5</p>
                        <p><span className="font-semibold">Target Score:</span> {statistics.overallScores.dataInfrastructure.target}/5</p>
                        <p><span className="font-semibold">Gap:</span> {statistics.overallScores.dataInfrastructure.gap}</p>
                        <p><span className="font-semibold">Readiness:</span> {statistics.overallScores.dataInfrastructure.percentage}%</p>
                    </div>

                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={categoryScoreData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis domain={[0, 5]} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="current" name="Current Status" fill="#8884d8" />
                            <Bar dataKey="target" name="Target" fill="#82ca9d" />
                            <Bar dataKey="gap" name="Gap" fill="#ff8042" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-4">Data Infrastructure Items</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="py-2 px-3 border-b text-left">Category</th>
                                    <th className="py-2 px-3 border-b text-left">Item</th>
                                    <th className="py-2 px-3 border-b text-left">Details</th>
                                    <th className="py-2 px-3 border-b text-center">Status</th>
                                    <th className="py-2 px-3 border-b text-center">Target</th>
                                    <th className="py-2 px-3 border-b text-center">Gap</th>
                                    <th className="py-2 px-3 border-b text-center">Priority</th>
                                    <th className="py-2 px-3 border-b text-left">Responsible</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dataItems.map((item, index) => (
                                    <tr key={`data-${index}`} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                                        <td className="py-2 px-3 border-b">{item.category}</td>
                                        <td className="py-2 px-3 border-b">{item.item}</td>
                                        <td className="py-2 px-3 border-b">{item.details}</td>
                                        <td className="py-2 px-3 border-b text-center">{item.current_status}</td>
                                        <td className="py-2 px-3 border-b text-center">{item.target}</td>
                                        <td className="py-2 px-3 border-b text-center">{item.target - item.current_status}</td>
                                        <td className="py-2 px-3 border-b text-center">
                                            <span className={`inline-block rounded-full w-6 h-6 text-white text-xs flex items-center justify-center ${item.priority === 'H' ? 'bg-red-500' :
                                                item.priority === 'M' ? 'bg-yellow-500' :
                                                    item.priority === 'L' ? 'bg-green-500' : 'bg-gray-400'
                                                }`}>
                                                {item.priority || '-'}
                                            </span>
                                        </td>
                                        <td className="py-2 px-3 border-b">{item.responsible}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    const renderGapsSection = () => {
        if (isComparisonMode) {
            // Prepare gap comparison data
            const gapComparison = comparisonData.map(data => ({
                name: data.assessment.assessment_name,
                totalGaps: data.statistics.gaps.length,
                highPriority: data.statistics.gaps.filter(gap => gap.priority === 'H').length,
                mediumPriority: data.statistics.gaps.filter(gap => gap.priority === 'M').length,
                lowPriority: data.statistics.gaps.filter(gap => gap.priority === 'L').length,
                hardwareGaps: data.statistics.gaps.filter(gap => gap.section === 'hardware').length,
                networkGaps: data.statistics.gaps.filter(gap => gap.section === 'network').length,
                softwareGaps: data.statistics.gaps.filter(gap => gap.section === 'software').length,
                dataInfraGaps: data.statistics.gaps.filter(gap => gap.section === 'dataInfrastructure').length
            }));

            return (
                <div>
                    <div className="bg-white p-6 rounded-lg shadow mb-6">
                        <h3 className="text-xl font-semibold mb-4">Technology Gaps Comparison</h3>

                        <div className="overflow-x-auto mb-6">
                            <table className="min-w-full bg-white">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="py-2 px-3 border-b">Assessment</th>
                                        <th className="py-2 px-3 border-b text-center">Total Gaps</th>
                                        <th className="py-2 px-3 border-b text-center">High Priority</th>
                                        <th className="py-2 px-3 border-b text-center">Medium Priority</th>
                                        <th className="py-2 px-3 border-b text-center">Low Priority</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {gapComparison.map((comparison, index) => (
                                        <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                                            <td className="py-2 px-3 border-b font-semibold">{comparison.name}</td>
                                            <td className="py-2 px-3 border-b text-center">{comparison.totalGaps}</td>
                                            <td className="py-2 px-3 border-b text-center">
                                                <span className="inline-block bg-red-500 text-white rounded-full px-3 py-1">
                                                    {comparison.highPriority}
                                                </span>
                                            </td>
                                            <td className="py-2 px-3 border-b text-center">
                                                <span className="inline-block bg-yellow-500 text-white rounded-full px-3 py-1">
                                                    {comparison.mediumPriority}
                                                </span>
                                            </td>
                                            <td className="py-2 px-3 border-b text-center">
                                                <span className="inline-block bg-green-500 text-white rounded-full px-3 py-1">
                                                    {comparison.lowPriority}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h4 className="text-lg font-semibold mb-2">Priority Distribution Comparison</h4>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={gapComparison}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="name"
                                            tickFormatter={(value) => {
                                                if (value.includes('_')) {
                                                    return value.split('_')[0];
                                                }
                                                return value;
                                            }}
                                        />
                                        <YAxis />
                                        <Tooltip
                                            labelFormatter={(label) => {
                                                if (label.includes('_')) {
                                                    return label.split('_')[0];
                                                }
                                                return label;
                                            }}
                                        />
                                        <Legend />
                                        <Bar dataKey="highPriority" name="High Priority" fill={PRIORITY_COLORS.H} />
                                        <Bar dataKey="mediumPriority" name="Medium Priority" fill={PRIORITY_COLORS.M} />
                                        <Bar dataKey="lowPriority" name="Low Priority" fill={PRIORITY_COLORS.L} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            <div>
                                <h4 className="text-lg font-semibold mb-2">Gaps by Section Comparison</h4>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={gapComparison}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="name"
                                            tickFormatter={(value) => {
                                                if (value.includes('_')) {
                                                    return value.split('_')[0];
                                                }
                                                return value;
                                            }}
                                        />
                                        <YAxis />
                                        <Tooltip
                                            labelFormatter={(label) => {
                                                if (label.includes('_')) {
                                                    return label.split('_')[0];
                                                }
                                                return label;
                                            }}
                                        />
                                        <Legend />
                                        <Bar dataKey="hardwareGaps" name="Hardware" fill={SECTION_COLORS.hardware} />
                                        <Bar dataKey="networkGaps" name="Network" fill={SECTION_COLORS.network} />
                                        <Bar dataKey="softwareGaps" name="Software" fill={SECTION_COLORS.software} />
                                        <Bar dataKey="dataInfraGaps" name="Data Infrastructure" fill={SECTION_COLORS.dataInfrastructure} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (!selectedAssessment) return null;

        const { statistics } = selectedAssessment;
        const { gaps } = statistics;

        // Group gaps by priority
        const highPriorityGaps = gaps.filter(gap => gap.priority === 'H');
        const mediumPriorityGaps = gaps.filter(gap => gap.priority === 'M');
        const lowPriorityGaps = gaps.filter(gap => gap.priority === 'L');

        // Group gaps by section
        const gapsBySection = gaps.reduce((acc, gap) => {
            if (!acc[gap.section]) {
                acc[gap.section] = [];
            }
            acc[gap.section].push(gap);
            return acc;
        }, {});

        return (
            <div>
                {renderSuccessMessage()}
                {renderErrorMessage()}
                {renderDeleteConfirmModal()}

                <div className="bg-white p-6 rounded-lg shadow mb-6">
                    <h3 className="text-xl font-semibold mb-4">Technology Gaps Overview</h3>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                        <div className="border rounded p-4 bg-gray-50">
                            <h4 className="text-lg font-semibold mb-2">Total Gaps</h4>
                            <p className="text-3xl font-bold text-center">{gaps.length}</p>
                        </div>

                        <div className="border rounded p-4 bg-red-50">
                            <h4 className="text-lg font-semibold mb-2 text-red-700">High Priority</h4>
                            <p className="text-3xl font-bold text-center text-red-700">{highPriorityGaps.length}</p>
                        </div>

                        <div className="border rounded p-4 bg-yellow-50">
                            <h4 className="text-lg font-semibold mb-2 text-yellow-700">Medium Priority</h4>
                            <p className="text-3xl font-bold text-center text-yellow-700">{mediumPriorityGaps.length}</p>
                        </div>

                        <div className="border rounded p-4 bg-green-50">
                            <h4 className="text-lg font-semibold mb-2 text-green-700">Low Priority</h4>
                            <p className="text-3xl font-bold text-center text-green-700">{lowPriorityGaps.length}</p>
                        </div>
                    </div>

                    <div className="mb-6">
                        <h4 className="text-lg font-semibold mb-2">Gaps by Section</h4>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                                data={Object.entries(gapsBySection).map(([section, sectionGaps]) => ({
                                    name: formatSectionName(section),
                                    High: sectionGaps.filter(gap => gap.priority === 'H').length,
                                    Medium: sectionGaps.filter(gap => gap.priority === 'M').length,
                                    Low: sectionGaps.filter(gap => gap.priority === 'L').length,
                                }))}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="High" stackId="a" fill={PRIORITY_COLORS.H} />
                                <Bar dataKey="Medium" stackId="a" fill={PRIORITY_COLORS.M} />
                                <Bar dataKey="Low" stackId="a" fill={PRIORITY_COLORS.L} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-4">All Technology Gaps</h3>

                    <div className="mb-4">
                        <h4 className="text-md font-semibold mb-2 bg-red-100 p-2 text-red-800">High Priority Gaps</h4>
                        {highPriorityGaps.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="min-w-full bg-white">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="py-2 px-3 border-b text-left">Section</th>
                                            <th className="py-2 px-3 border-b text-left">Category</th>
                                            <th className="py-2 px-3 border-b text-left">Item</th>
                                            <th className="py-2 px-3 border-b text-center">Current</th>
                                            <th className="py-2 px-3 border-b text-center">Target</th>
                                            <th className="py-2 px-3 border-b text-center">Gap</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {highPriorityGaps.map((gap, index) => (
                                            <tr key={`high-gap-${index}`} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                                                <td className="py-2 px-3 border-b">{formatSectionName(gap.section)}</td>
                                                <td className="py-2 px-3 border-b">{gap.category}</td>
                                                <td className="py-2 px-3 border-b">{gap.item}</td>
                                                <td className="py-2 px-3 border-b text-center">{gap.current_status}</td>
                                                <td className="py-2 px-3 border-b text-center">{gap.target}</td>
                                                <td className="py-2 px-3 border-b text-center font-semibold">{gap.gap}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="text-center py-4">No high priority gaps found.</p>
                        )}
                    </div>

                    <div className="mb-4">
                        <h4 className="text-md font-semibold mb-2 bg-yellow-100 p-2 text-yellow-800">Medium Priority Gaps</h4>
                        {mediumPriorityGaps.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="min-w-full bg-white">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="py-2 px-3 border-b text-left">Section</th>
                                            <th className="py-2 px-3 border-b text-left">Category</th>
                                            <th className="py-2 px-3 border-b text-left">Item</th>
                                            <th className="py-2 px-3 border-b text-center">Current</th>
                                            <th className="py-2 px-3 border-b text-center">Target</th>
                                            <th className="py-2 px-3 border-b text-center">Gap</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {mediumPriorityGaps.map((gap, index) => (
                                            <tr key={`medium-gap-${index}`} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                                                <td className="py-2 px-3 border-b">{formatSectionName(gap.section)}</td>
                                                <td className="py-2 px-3 border-b">{gap.category}</td>
                                                <td className="py-2 px-3 border-b">{gap.item}</td>
                                                <td className="py-2 px-3 border-b text-center">{gap.current_status}</td>
                                                <td className="py-2 px-3 border-b text-center">{gap.target}</td>
                                                <td className="py-2 px-3 border-b text-center font-semibold">{gap.gap}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="text-center py-4">No medium priority gaps found.</p>
                        )}
                    </div>

                    <div>
                        <h4 className="text-md font-semibold mb-2 bg-green-100 p-2 text-green-800">Low Priority Gaps</h4>
                        {lowPriorityGaps.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="min-w-full bg-white">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="py-2 px-3 border-b text-left">Section</th>
                                            <th className="py-2 px-3 border-b text-left">Category</th>
                                            <th className="py-2 px-3 border-b text-left">Item</th>
                                            <th className="py-2 px-3 border-b text-center">Current</th>
                                            <th className="py-2 px-3 border-b text-center">Target</th>
                                            <th className="py-2 px-3 border-b text-center">Gap</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lowPriorityGaps.map((gap, index) => (
                                            <tr key={`low-gap-${index}`} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                                                <td className="py-2 px-3 border-b">{formatSectionName(gap.section)}</td>
                                                <td className="py-2 px-3 border-b">{gap.category}</td>
                                                <td className="py-2 px-3 border-b">{gap.item}</td>
                                                <td className="py-2 px-3 border-b text-center">{gap.current_status}</td>
                                                <td className="py-2 px-3 border-b text-center">{gap.target}</td>
                                                <td className="py-2 px-3 border-b text-center font-semibold">{gap.gap}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="text-center py-4">No low priority gaps found.</p>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderBudgetSection = () => {
        if (isComparisonMode) {
            // Prepare budget comparison data
            const budgetComparison = comparisonData.map(data => {
                const hardwareBudget = parseFloat(data.budget.hardware_budget) || 0;
                const networkBudget = parseFloat(data.budget.network_budget) || 0;
                const softwareBudget = parseFloat(data.budget.software_budget) || 0;
                const dataInfraBudget = parseFloat(data.budget.data_infrastructure_budget) || 0;
                const totalBudget = hardwareBudget + networkBudget + softwareBudget + dataInfraBudget;

                return {
                    name: data.assessment.assessment_name,
                    hardware: hardwareBudget,
                    network: networkBudget,
                    software: softwareBudget,
                    dataInfra: dataInfraBudget,
                    total: totalBudget,
                    hardwarePercentage: hardwareBudget > 0 ? ((hardwareBudget / totalBudget) * 100).toFixed(1) : 0,
                    networkPercentage: networkBudget > 0 ? ((networkBudget / totalBudget) * 100).toFixed(1) : 0,
                    softwarePercentage: softwareBudget > 0 ? ((softwareBudget / totalBudget) * 100).toFixed(1) : 0,
                    dataInfraPercentage: dataInfraBudget > 0 ? ((dataInfraBudget / totalBudget) * 100).toFixed(1) : 0
                };
            });

            return (
                <div>
                    <div className="bg-white p-6 rounded-lg shadow mb-6">
                        <h3 className="text-xl font-semibold mb-4">Budget Comparison</h3>

                        <div className="overflow-x-auto mb-6">
                            <table className="min-w-full bg-white">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="py-2 px-3 border-b">Assessment</th>
                                        <th className="py-2 px-3 border-b text-center">Total Budget</th>
                                        <th className="py-2 px-3 border-b text-center">Hardware</th>
                                        <th className="py-2 px-3 border-b text-center">Network</th>
                                        <th className="py-2 px-3 border-b text-center">Software</th>
                                        <th className="py-2 px-3 border-b text-center">Data Infrastructure</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {budgetComparison.map((comparison, index) => (
                                        <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                                            <td className="py-2 px-3 border-b font-semibold">{comparison.name}</td>
                                            <td className="py-2 px-3 border-b text-center font-bold">{comparison.total.toLocaleString()} บาท</td>
                                            <td className="py-2 px-3 border-b text-center">
                                                {comparison.hardware.toLocaleString()} บาท
                                                <div className="text-xs text-gray-500">({comparison.hardwarePercentage}%)</div>
                                            </td>
                                            <td className="py-2 px-3 border-b text-center">
                                                {comparison.network.toLocaleString()} บาท
                                                <div className="text-xs text-gray-500">({comparison.networkPercentage}%)</div>
                                            </td>
                                            <td className="py-2 px-3 border-b text-center">
                                                {comparison.software.toLocaleString()} บาท
                                                <div className="text-xs text-gray-500">({comparison.softwarePercentage}%)</div>
                                            </td>
                                            <td className="py-2 px-3 border-b text-center">
                                                {comparison.dataInfra.toLocaleString()} บาท
                                                <div className="text-xs text-gray-500">({comparison.dataInfraPercentage}%)</div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h4 className="text-lg font-semibold mb-2">Total Budget Comparison</h4>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={budgetComparison}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis tickFormatter={(value) => value.toLocaleString()} />
                                        <Tooltip formatter={(value) => value.toLocaleString() + ' บาท'} />
                                        <Legend />
                                        <Bar dataKey="total" name="Total Budget" fill="#8884d8" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            <div>
                                <h4 className="text-lg font-semibold mb-2">Budget Breakdown Comparison</h4>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={budgetComparison}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis tickFormatter={(value) => value.toLocaleString()} />
                                        <Tooltip formatter={(value) => value.toLocaleString() + ' บาท'} />
                                        <Legend />
                                        <Bar dataKey="hardware" name="Hardware" stackId="a" fill={SECTION_COLORS.hardware} />
                                        <Bar dataKey="network" name="Network" stackId="a" fill={SECTION_COLORS.network} />
                                        <Bar dataKey="software" name="Software" stackId="a" fill={SECTION_COLORS.software} />
                                        <Bar dataKey="dataInfra" name="Data Infrastructure" stackId="a" fill={SECTION_COLORS.dataInfrastructure} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (!selectedAssessment) return null;

        const { budget } = selectedAssessment;

        // Calculate total budget
        const hardwareBudget = parseFloat(budget.hardware_budget) || 0;
        const networkBudget = parseFloat(budget.network_budget) || 0;
        const softwareBudget = parseFloat(budget.software_budget) || 0;
        const dataInfraBudget = parseFloat(budget.data_infrastructure_budget) || 0;
        const totalBudget = hardwareBudget + networkBudget + softwareBudget + dataInfraBudget;

        // Prepare data for charts
        const budgetData = [
            { name: 'Hardware', value: hardwareBudget },
            { name: 'Network', value: networkBudget },
            { name: 'Software', value: softwareBudget },
            { name: 'Data Infrastructure', value: dataInfraBudget }
        ];

        const budgetBarData = [
            { name: 'Hardware', value: hardwareBudget, percentage: (hardwareBudget / totalBudget * 100).toFixed(1) },
            { name: 'Network', value: networkBudget, percentage: (networkBudget / totalBudget * 100).toFixed(1) },
            { name: 'Software', value: softwareBudget, percentage: (softwareBudget / totalBudget * 100).toFixed(1) },
            { name: 'Data Infrastructure', value: dataInfraBudget, percentage: (dataInfraBudget / totalBudget * 100).toFixed(1) }
        ];

        return (
            <div>
                {renderSuccessMessage()}
                {renderErrorMessage()}
                {renderDeleteConfirmModal()}

                <div className="bg-white p-6 rounded-lg shadow mb-6">
                    <h3 className="text-xl font-semibold mb-4">Budget Overview</h3>

                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                        <div className="border rounded p-4 bg-gray-50 md:col-span-1">
                            <h4 className="text-lg font-semibold mb-2">Total Budget</h4>
                            <p className="text-3xl font-bold text-center">{totalBudget.toLocaleString()} บาท</p>
                        </div>

                        <div className="border rounded p-4 bg-blue-50 md:col-span-1">
                            <h4 className="text-md font-semibold mb-2">Hardware</h4>
                            <p className="text-xl font-bold text-center">{hardwareBudget.toLocaleString()} บาท</p>
                            <p className="text-center text-sm">({(hardwareBudget / totalBudget * 100).toFixed(1)}%)</p>
                        </div>

                        <div className="border rounded p-4 bg-green-50 md:col-span-1">
                            <h4 className="text-md font-semibold mb-2">Network</h4>
                            <p className="text-xl font-bold text-center">{networkBudget.toLocaleString()} บาท</p>
                            <p className="text-center text-sm">({(networkBudget / totalBudget * 100).toFixed(1)}%)</p>
                        </div>

                        <div className="border rounded p-4 bg-yellow-50 md:col-span-1">
                            <h4 className="text-md font-semibold mb-2">Software</h4>
                            <p className="text-xl font-bold text-center">{softwareBudget.toLocaleString()} บาท</p>
                            <p className="text-center text-sm">({(softwareBudget / totalBudget * 100).toFixed(1)}%)</p>
                        </div>

                        <div className="border rounded p-4 bg-purple-50 md:col-span-1">
                            <h4 className="text-md font-semibold mb-2">Data-Infra</h4>
                            <p className="text-xl font-bold text-center">{dataInfraBudget.toLocaleString()} บาท</p>
                            <p className="text-center text-sm">({(dataInfraBudget / totalBudget * 100).toFixed(1)}%)</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <h4 className="text-lg font-semibold mb-2">Budget Distribution</h4>
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={budgetData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={true}
                                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={80}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {budgetData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => value.toLocaleString() + ' บาท'} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>

                        <div>
                            <h4 className="text-lg font-semibold mb-2">Budget Comparison</h4>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={budgetBarData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis tickFormatter={(value) => value.toLocaleString()} />
                                    <Tooltip formatter={(value) => value.toLocaleString() + ' บาท'} />
                                    <Legend />
                                    <Bar dataKey="value" fill="#8884d8" name="Budget (บาท)" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-4">Budget Details</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <h4 className="text-md font-semibold mb-2 bg-blue-100 p-2">Hardware Budget</h4>
                            <p className="text-xl font-bold mb-2">{hardwareBudget.toLocaleString()} บาท</p>
                            <div className="border p-3 bg-gray-50 min-h-[100px]">
                                <h5 className="font-semibold mb-1">Details:</h5>
                                <p>{budget.hardware_details || 'No details provided.'}</p>
                            </div>
                        </div>

                        <div>
                            <h4 className="text-md font-semibold mb-2 bg-green-100 p-2">Network Budget</h4>
                            <p className="text-xl font-bold mb-2">{networkBudget.toLocaleString()} บาท</p>
                            <div className="border p-3 bg-gray-50 min-h-[100px]">
                                <h5 className="font-semibold mb-1">Details:</h5>
                                <p>{budget.network_details || 'No details provided.'}</p>
                            </div>
                        </div>

                        <div>
                            <h4 className="text-md font-semibold mb-2 bg-yellow-100 p-2">Software Budget</h4>
                            <p className="text-xl font-bold mb-2">{softwareBudget.toLocaleString()} บาท</p>
                            <div className="border p-3 bg-gray-50 min-h-[100px]">
                                <h5 className="font-semibold mb-1">Details:</h5>
                                <p>{budget.software_details || 'No details provided.'}</p>
                            </div>
                        </div>

                        <div>
                            <h4 className="text-md font-semibold mb-2 bg-purple-100 p-2">Data Infrastructure Budget</h4>
                            <p className="text-xl font-bold mb-2">{dataInfraBudget.toLocaleString()} บาท</p>
                            <div className="border p-3 bg-gray-50 min-h-[100px]">
                                <h5 className="font-semibold mb-1">Details:</h5>
                                <p>{budget.data_infrastructure_details || 'No details provided.'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="max-w-full bg-white rounded shadow">
            <div className="p-4 bg-blue-600 text-white">
                <div className="flex justify-between items-center">
                    <h2 className="text-xl font-bold text-white">Digital Transformation Dashboard</h2>

                    {(viewMode === 'detail' || isComparisonMode && comparisonData.length > 0) && (
                        <div className="flex items-center">
                            {isComparisonMode && comparisonData.length > 0 ? (
                                <span className="mr-2">Comparing {comparisonData.length} assessments</span>
                            ) : selectedAssessment ? (
                                <>
                                    <span className="mr-2">Assessment:</span>
                                    <select
                                        className="bg-white text-blue-800 px-3 py-1 rounded"
                                        value={selectedAssessmentId || ""}
                                        onChange={(e) => onAssessmentSelect(e.target.value)}
                                    >
                                        {assessments.map(assessment => (
                                            <option key={assessment.id} value={assessment.id}>
                                                {assessment.assessment_name} - {assessment.company_name}
                                            </option>
                                        ))}
                                    </select>
                                </>
                            ) : null}

                            <button
                                className={`ml-4 px-4 py-1 ${isComparisonMode ? 'bg-red-500 hover:bg-red-600' : 'bg-yellow-500 hover:bg-yellow-600'} text-white rounded`}
                                onClick={() => {
                                    toggleComparisonMode();
                                    setViewMode('list'); // Go back to list view
                                }}
                            >
                                {isComparisonMode ? 'Exit Comparison Mode' : 'Compare Assessments'}
                            </button>

                            <button
                                className="ml-4 px-4 py-1 bg-gray-200 text-blue-800 rounded hover:bg-gray-300"
                                onClick={() => setViewMode('list')}
                            >
                                Back to List
                            </button>

                            <button
                                className="ml-4 px-4 py-1 bg-white text-blue-800 rounded hover:bg-gray-100"
                                onClick={onCreateNewAssessment}
                            >
                                New Assessment
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {viewMode === 'list' ? (
                renderAssessmentList()
            ) : (
                <>
                    {(selectedAssessment || (isComparisonMode && comparisonData.length > 0)) && (
                        <div className="bg-gray-100 p-2 flexoverflow-x-auto">
                            <button
                                className={`px-4 py-2 font-semibold rounded-t ${activeTab === 'overview' ? 'bg-white' : 'bg-gray-200'}`}
                                onClick={() => handleTabChange('overview')}
                            >
                                Overview
                            </button>
                            <button
                                className={`px-4 py-2 font-semibold rounded-t ${activeTab === 'hardware' ? 'bg-white' : 'bg-gray-200'}`}
                                onClick={() => handleTabChange('hardware')}
                            >
                                Hardware
                            </button>
                            <button
                                className={`px-4 py-2 font-semibold rounded-t ${activeTab === 'network' ? 'bg-white' : 'bg-gray-200'}`}
                                onClick={() => handleTabChange('network')}
                            >
                                Network
                            </button>
                            <button
                                className={`px-4 py-2 font-semibold rounded-t ${activeTab === 'software' ? 'bg-white' : 'bg-gray-200'}`}
                                onClick={() => handleTabChange('software')}
                            >
                                Software
                            </button>
                            <button
                                className={`px-4 py-2 font-semibold rounded-t ${activeTab === 'dataInfrastructure' ? 'bg-white' : 'bg-gray-200'}`}
                                onClick={() => handleTabChange('dataInfrastructure')}
                            >
                                Data Infrastructure
                            </button>
                            <button
                                className={`px-4 py-2 font-semibold rounded-t ${activeTab === 'gaps' ? 'bg-white' : 'bg-gray-200'}`}
                                onClick={() => handleTabChange('gaps')}
                            >
                                Technology Gaps
                            </button>
                            <button
                                className={`px-4 py-2 font-semibold rounded-t ${activeTab === 'budget' ? 'bg-white' : 'bg-gray-200'}`}
                                onClick={() => handleTabChange('budget')}
                            >
                                Budget
                            </button>
                        </div>
                    )}

                    <div className="p-4">
                        {activeTab === 'overview' && renderOverview()}
                        {activeTab === 'hardware' && renderHardwareSection()}
                        {activeTab === 'network' && renderNetworkSection()}
                        {activeTab === 'software' && renderSoftwareSection()}
                        {activeTab === 'dataInfrastructure' && renderDataInfrastructureSection()}
                        {activeTab === 'gaps' && renderGapsSection()}
                        {activeTab === 'budget' && renderBudgetSection()}
                    </div>
                </>
            )}
        </div>
    );
};

export default DigitalTransformationDashboard;